import {
  getStyleById,
  ProductKey,
  PRODUCT_LABELS,
  StyleDefinition,
} from './style-definitions';
import {
  LOCKED_SELECTED_STYLES,
  normalizeSelectedStyles,
  readPersistedSelectedStyles,
  SelectedStyles,
} from './selection';

export interface SemanticTokens {
  product: ProductKey;
  productLabel: string;
  styleId: string;
  styleName: string;
  density: StyleDefinition['tokens']['density'];
  colors: {
    primary: string;
    background: string;
    surface: string;
    text: string;
    muted: string;
    border: string;
    success: string;
    warning: string;
    danger: string;
    accentSoft: string;
  };
  spacing: {
    base: number;
    xs: number;
    sm: number;
    md: number;
    lg: number;
    xl: number;
    xxl: number;
  };
  radius: {
    sm: number;
    md: number;
    lg: number;
    pill: number;
  };
  typography: {
    fontFamily: string;
    headingFamily: string;
    displaySize: number;
    titleSize: number;
    bodySize: number;
    captionSize: number;
    lineHeight: number;
    letterSpacing: string;
    fontWeightRegular: number;
    fontWeightStrong: number;
  };
}

const typographyPresets: Record<string, SemanticTokens['typography']> = {
  'soft-neutral-pro': {
    fontFamily: '"Inter", "Segoe UI", sans-serif',
    headingFamily: '"Inter", "Segoe UI", sans-serif',
    displaySize: 34,
    titleSize: 20,
    bodySize: 15,
    captionSize: 13,
    lineHeight: 1.5,
    letterSpacing: '-0.01em',
    fontWeightRegular: 500,
    fontWeightStrong: 700,
  },
  'data-grid-focus': {
    fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
    headingFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
    displaySize: 32,
    titleSize: 19,
    bodySize: 14,
    captionSize: 12,
    lineHeight: 1.45,
    letterSpacing: '-0.015em',
    fontWeightRegular: 500,
    fontWeightStrong: 700,
  },
  'executive-dashboard': {
    fontFamily: '"Aptos", "Inter", sans-serif',
    headingFamily: '"Aptos Display", "Inter", sans-serif',
    displaySize: 36,
    titleSize: 21,
    bodySize: 15,
    captionSize: 13,
    lineHeight: 1.5,
    letterSpacing: '-0.02em',
    fontWeightRegular: 500,
    fontWeightStrong: 700,
  },
  'dark-control-room': {
    fontFamily: '"Sora", "Inter", sans-serif',
    headingFamily: '"Sora", "Inter", sans-serif',
    displaySize: 34,
    titleSize: 20,
    bodySize: 15,
    captionSize: 13,
    lineHeight: 1.45,
    letterSpacing: '-0.015em',
    fontWeightRegular: 500,
    fontWeightStrong: 700,
  },
  'minimal-mono': {
    fontFamily: '"IBM Plex Sans", "Inter", sans-serif',
    headingFamily: '"IBM Plex Sans", "Inter", sans-serif',
    displaySize: 33,
    titleSize: 19,
    bodySize: 15,
    captionSize: 12,
    lineHeight: 1.5,
    letterSpacing: '-0.025em',
    fontWeightRegular: 500,
    fontWeightStrong: 700,
  },
  'field-clear': {
    fontFamily: '"Inter", "Segoe UI", sans-serif',
    headingFamily: '"Inter", "Segoe UI", sans-serif',
    displaySize: 32,
    titleSize: 22,
    bodySize: 16,
    captionSize: 13,
    lineHeight: 1.45,
    letterSpacing: '-0.015em',
    fontWeightRegular: 600,
    fontWeightStrong: 700,
  },
  'construction-rugged': {
    fontFamily: '"Barlow", "Inter", sans-serif',
    headingFamily: '"Barlow Condensed", "Barlow", sans-serif',
    displaySize: 34,
    titleSize: 22,
    bodySize: 16,
    captionSize: 13,
    lineHeight: 1.4,
    letterSpacing: '-0.01em',
    fontWeightRegular: 600,
    fontWeightStrong: 700,
  },
  'friendly-flow': {
    fontFamily: '"Nunito Sans", "Inter", sans-serif',
    headingFamily: '"Nunito Sans", "Inter", sans-serif',
    displaySize: 33,
    titleSize: 22,
    bodySize: 16,
    captionSize: 13,
    lineHeight: 1.5,
    letterSpacing: '-0.01em',
    fontWeightRegular: 600,
    fontWeightStrong: 700,
  },
  'kiosk-mode': {
    fontFamily: '"Aptos", "Inter", sans-serif',
    headingFamily: '"Aptos", "Inter", sans-serif',
    displaySize: 36,
    titleSize: 24,
    bodySize: 17,
    captionSize: 13,
    lineHeight: 1.45,
    letterSpacing: '-0.015em',
    fontWeightRegular: 600,
    fontWeightStrong: 700,
  },
  'emergency-priority': {
    fontFamily: '"Public Sans", "Inter", sans-serif',
    headingFamily: '"Public Sans", "Inter", sans-serif',
    displaySize: 34,
    titleSize: 23,
    bodySize: 16,
    captionSize: 13,
    lineHeight: 1.4,
    letterSpacing: '-0.01em',
    fontWeightRegular: 600,
    fontWeightStrong: 800,
  },
};

function hexToRgb(hex: string) {
  const value = hex.replace('#', '');
  const normalized = value.length === 3
    ? value.split('').map((part) => `${part}${part}`).join('')
    : value;
  const int = parseInt(normalized, 16);

  return {
    r: (int >> 16) & 255,
    g: (int >> 8) & 255,
    b: int & 255,
  };
}

function withAlpha(hex: string, alpha: number) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function buildSpacing(base: number, density: StyleDefinition['tokens']['density']) {
  const multiplier = density === 'high' ? 0.75 : density === 'low' ? 1.25 : 1;
  const scaled = (step: number) => Math.round(base * step * multiplier);

  return {
    base,
    xs: scaled(0.5),
    sm: scaled(1),
    md: scaled(2),
    lg: scaled(3),
    xl: scaled(4),
    xxl: scaled(6),
  };
}

export function buildSemanticTokens(style: StyleDefinition, product: ProductKey): SemanticTokens {
  const typography = typographyPresets[style.id] ?? typographyPresets['soft-neutral-pro'];

  return {
    product,
    productLabel: PRODUCT_LABELS[product],
    styleId: style.id,
    styleName: style.name,
    density: style.tokens.density,
    colors: {
      ...style.tokens.colors,
      border: withAlpha(style.tokens.colors.text, style.tokens.density === 'high' ? 0.18 : 0.12),
      accentSoft: withAlpha(style.tokens.colors.primary, style.category === 'field-operations' ? 0.14 : 0.1),
    },
    spacing: buildSpacing(style.tokens.spacing, style.tokens.density),
    radius: {
      sm: Math.max(6, Math.round(style.tokens.radius * 0.5)),
      md: style.tokens.radius,
      lg: Math.round(style.tokens.radius * 1.35),
      pill: 999,
    },
    typography,
  };
}

export function getTokens(selectedStyles?: Partial<SelectedStyles>) {
  const resolved = normalizeSelectedStyles(selectedStyles ?? readPersistedSelectedStyles());

  return {
    projectManagerApp: buildSemanticTokens(
      getStyleById(resolved.projectManagerApp) ?? getStyleById(LOCKED_SELECTED_STYLES.projectManagerApp)!,
      'projectManagerApp',
    ),
    checkInPlatform: buildSemanticTokens(
      getStyleById(resolved.checkInPlatform) ?? getStyleById(LOCKED_SELECTED_STYLES.checkInPlatform)!,
      'checkInPlatform',
    ),
  };
}

export const tokens = getTokens(LOCKED_SELECTED_STYLES);
